import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, TrendingUp, Loader2, ExternalLink, Award, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { bcAssessmentGet } from "@/lib/api";

const BAND_STYLES = {
  "AI-native":  { bg: "from-emerald-500 to-emerald-700", label: "AI-native — you're leading",   emoji: "★" },
  "AI-ready":   { bg: "from-indigo-500 to-indigo-700",   label: "AI-ready — momentum on your side", emoji: "▲" },
  "AI-curious": { bg: "from-amber-500 to-amber-700",     label: "AI-curious — early wins ahead",    emoji: "●" },
  "AI-nascent": { bg: "from-rose-500 to-rose-700",       label: "AI-nascent — biggest opportunity", emoji: "◆" },
};

export default function BusinessCenterAssessmentReport() {
  const { aid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    document.title = "Your AI Readiness Report | WebsiteTech.pro";
    bcAssessmentGet(aid).then(setData).catch((e) => setErr(e?.response?.data?.detail || e?.message));
  }, [aid]);

  if (err) return <Shell><div className="p-8 text-red-600" data-testid="bc-report-err">Could not load your report: {err}</div></Shell>;
  if (!data || !data.result) return <Shell><div className="p-16 text-center text-slate-500"><Loader2 className="w-8 h-8 mx-auto animate-spin" /><div className="mt-4">Loading your report…</div></div></Shell>;

  const r = data.result;
  const a = data.assessment;
  const band = BAND_STYLES[r.band] || BAND_STYLES["AI-curious"];

  return (
    <Shell>
      <div className="max-w-5xl mx-auto px-4 pb-16" data-testid="bc-report">
        <div className="mb-4 mt-6 text-xs uppercase tracking-widest text-indigo-700 font-bold flex items-center gap-2">
          <Link to="/business-center" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Business Center</Link>
          <span className="text-slate-300">·</span>
          <Link to="/business-center/assessment" className="hover:text-indigo-900">Assessment</Link>
        </div>

        {/* Score card */}
        <section className={`rounded-3xl overflow-hidden shadow-xl border border-white/10 bg-gradient-to-br ${band.bg} text-white`}>
          <div className="p-8 md:p-10">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold opacity-80">
              <Award className="w-4 h-4" /> AI Readiness Score
            </div>
            <div className="mt-2 flex items-baseline gap-4">
              <div className="text-7xl md:text-8xl font-black tabular-nums" data-testid="bc-report-score">{Math.round(r.overall)}</div>
              <div className="text-2xl md:text-3xl font-semibold opacity-90">/ 100</div>
            </div>
            <div className="mt-2 text-lg md:text-xl font-semibold opacity-90" data-testid="bc-report-band">{band.emoji} {band.label}</div>
            <div className="mt-4 text-sm opacity-80 max-w-2xl">
              {a?.business_name ? `Report for ${a.business_name}. ` : ""}
              Scored across 20 categories from your {(data.answers || []).length} answers. Recommendations are rule-based and disclose commercial relationships on every product review.
            </div>
          </div>
        </section>

        {/* Category grid */}
        <section className="mt-10">
          <h2 className="text-2xl font-bold text-slate-900">Category scores</h2>
          <p className="mt-1 text-sm text-slate-600">Every category is weighted. Focus where the score is low and the weight is high.</p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            {r.categories.map((c) => (
              <div key={c.key} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`bc-report-cat-${c.key}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900">{c.label}</div>
                  <div className="text-xs text-slate-500">weight {c.weight}</div>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="text-2xl font-bold text-slate-900 tabular-nums">{Math.round(c.score)}</div>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${c.score >= 75 ? "bg-emerald-500" : c.score >= 50 ? "bg-indigo-500" : c.score >= 30 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${c.score}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Quick wins + gaps + strengths */}
        <section className="mt-10 grid md:grid-cols-3 gap-4">
          <ListCard icon={TrendingUp} title="Quick wins" tone="indigo" items={r.quick_wins} testid="bc-report-quickwins" />
          <ListCard icon={AlertTriangle} title="Biggest gaps" tone="rose" items={r.gaps} testid="bc-report-gaps" />
          <ListCard icon={CheckCircle2} title="Strengths" tone="emerald" items={r.strengths} testid="bc-report-strengths" />
        </section>

        {/* Recommendations */}
        <section className="mt-10">
          <h2 className="text-2xl font-bold text-slate-900">Recommended solutions</h2>
          <p className="mt-1 text-sm text-slate-600">Rule-based — each recommendation cites the assessment category that produced it.</p>
          <div className="mt-6 space-y-3">
            {r.recommendations.map((rec, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-4" data-testid={`bc-report-rec-${i}`}>
                <div className={`inline-flex flex-shrink-0 w-8 h-8 rounded-full items-center justify-center text-white text-xs font-bold ${rec.priority === "immediate" ? "bg-rose-500" : rec.priority === "soon" ? "bg-amber-500" : "bg-indigo-500"}`}>{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2">
                    <div className="font-semibold text-slate-900">{rec.module}</div>
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${rec.priority === "immediate" ? "bg-rose-50 text-rose-800 border-rose-200" : rec.priority === "soon" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-indigo-50 text-indigo-800 border-indigo-200"}`}>{rec.priority}</span>
                  </div>
                  <div className="mt-1 text-sm text-slate-700">{rec.why}</div>
                  <div className="mt-1 text-xs text-slate-500">Based on your <strong>{rec.based_on_category}</strong> score of {Math.round(rec.current_score)}.</div>
                </div>
                <Link to={rec.route} className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:text-indigo-900">
                  Learn more <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            ))}
            {r.recommendations.length === 0 && (
              <div className="text-sm text-slate-500 bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center">Your scores are strong across the board — nothing urgent to change.</div>
            )}
          </div>
        </section>

        {/* Roadmap */}
        <section className="mt-10">
          <h2 className="text-2xl font-bold text-slate-900">Your 30 / 60 / 90-day roadmap</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {[
              { label: "First 30 days", items: r.roadmap?.day_30, key: "day_30", tone: "rose" },
              { label: "Days 31–60",    items: r.roadmap?.day_60, key: "day_60", tone: "amber" },
              { label: "Days 61–90",    items: r.roadmap?.day_90, key: "day_90", tone: "indigo" },
            ].map((s) => (
              <div key={s.key} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`bc-report-${s.key}`}>
                <div className={`text-xs uppercase tracking-widest font-bold ${s.tone === "rose" ? "text-rose-700" : s.tone === "amber" ? "text-amber-700" : "text-indigo-700"}`}>{s.label}</div>
                <ul className="mt-3 space-y-2">
                  {(s.items || []).map((it, i) => (
                    <li key={i} className="text-sm text-slate-800 border-l-2 border-slate-200 pl-3">
                      <div className="font-semibold">{it.module}</div>
                      <div className="text-xs text-slate-500">{it.why}</div>
                    </li>
                  ))}
                  {(s.items || []).length === 0 && <li className="text-xs text-slate-400 italic">Nothing scheduled — you're already handling this area well.</li>}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-12 rounded-3xl overflow-hidden bg-slate-950 text-white">
          <div className="p-8 md:p-10 grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest"><Sparkles className="w-3.5 h-3.5" /> Next step</div>
              <h2 className="mt-4 text-2xl md:text-3xl font-bold">Want WebsiteTech to implement this for you?</h2>
              <p className="mt-2 text-slate-300 text-sm">We turn this roadmap into a proposal — with exact modules, timeline and cost. Your report and answers are attached automatically.</p>
            </div>
            <div className="flex flex-col gap-2">
              <Link
                to={`/business-center/stack-builder?assessment_id=${aid}`}
                data-testid="bc-report-stack"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-5 py-3 transition"
              >
                Build my AI stack <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="bc-report-request"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-5 py-3 transition"
              >
                Request implementation
              </Link>
              <Link
                to="/business-center/methodology"
                className="text-center text-xs text-slate-400 hover:text-white transition"
              >
                How we scored this
              </Link>
            </div>
          </div>
        </section>

        <div className="mt-10 text-xs text-slate-400 border-t border-slate-200 pt-4">
          <strong>Disclaimer:</strong> This report is for planning guidance. No guaranteed savings, revenue, or compliance outcomes are implied. Every recommendation is auditable — see our published scoring methodology.
        </div>
      </div>
    </Shell>
  );
}

function ListCard({ icon: Icon, title, items, tone, testid }) {
  const colors = {
    indigo:  "border-indigo-200 bg-indigo-50 text-indigo-900",
    rose:    "border-rose-200 bg-rose-50 text-rose-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
  };
  return (
    <div className={`rounded-2xl border p-5 ${colors[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-sm font-bold"><Icon className="w-4 h-4" /> {title}</div>
      <ul className="mt-3 space-y-2 text-sm">
        {(items || []).map((it, i) => (
          <li key={i} className="flex items-center justify-between gap-2">
            <span className="truncate">{it.label}</span>
            <span className="text-xs opacity-70 tabular-nums">{Math.round(it.score)}</span>
          </li>
        ))}
        {(items || []).length === 0 && <li className="text-xs opacity-60 italic">No entries.</li>}
      </ul>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      <main className="flex-1 pt-24">{children}</main>
      <Footer />
    </div>
  );
}
